import { ApolloError, MutationFunction } from "@apollo/client";
import React, { ReactElement } from "react";
import CancelSubscription from "~/lib/mutations/CancelSubscription";
import ChangeSubscriptionPlan from "~/lib/mutations/ChangeSubscriptionPlan";
import {
  ICancelSubscriptionMutation,
  ICancelSubscriptionMutationVariables,
} from "~/lib/mutations/__generated__/CancelSubscription.generated";
import {
  IChangeSubscriptionPlanMutation,
  IChangeSubscriptionPlanMutationVariables,
} from "~/lib/mutations/__generated__/ChangeSubscriptionPlan.generated";
import { useSelfie } from "~/components/auth/Auth";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import { useRouter } from "next/router";
import { Mutation } from "@apollo/client/react/components";

interface IChangePlansMutationsChildrenState {
  changeSubscriptionPlan: MutationFunction<IChangeSubscriptionPlanMutation, IChangeSubscriptionPlanMutationVariables>;
  isChangingPlans: boolean;
  failedToCommitPlanChange: ApolloError | undefined;
  cancelSubscription: MutationFunction<ICancelSubscriptionMutation, ICancelSubscriptionMutationVariables>;
  isCancellingSubscription: boolean;
  cancelSubscriptionMutationResult: ICancelSubscriptionMutation | null | undefined;
  failedToCancelSubscription: ApolloError | undefined;
}

interface IChangePlansMutationsProps {
  children: (state: IChangePlansMutationsChildrenState) => ReactElement;
}

export default function ChangePlansMutations({ children }: IChangePlansMutationsProps) {
  const selfie = useSelfie();
  const router = useRouter();

  return (
    <Mutation<IChangeSubscriptionPlanMutation, IChangeSubscriptionPlanMutationVariables>
      mutation={ChangeSubscriptionPlan}
      onCompleted={() => {
        if (selfie && selfie.__typename === "VendorUser" && selfie.company) {
          router.push("/vendors/[vendorSlug]/[[...content]]", `/vendors/${selfie.company.slug}/account`);
        }
      }}
      refetchQueries={refetchQueries(["Vendor", "CompanyAccountBillingInfo", "Plans"])}
    >
      {(changeSubscriptionPlan, { loading: isChangingPlans, error: failedToCommitPlanChange }) => (
        <Mutation<ICancelSubscriptionMutation, ICancelSubscriptionMutationVariables>
          mutation={CancelSubscription}
          refetchQueries={refetchQueries(["CompanyAccountBillingInfo", "Vendor", "Plans"])}
        >
          {(
            cancelSubscription,
            {
              loading: isCancellingSubscription,
              error: failedToCancelSubscription,
              data: cancelSubscriptionMutationResult,
            },
          ) => {
            return children({
              changeSubscriptionPlan,
              isChangingPlans,
              failedToCommitPlanChange,
              cancelSubscription,
              isCancellingSubscription,
              cancelSubscriptionMutationResult: cancelSubscriptionMutationResult,
              failedToCancelSubscription,
            });
          }}
        </Mutation>
      )}
    </Mutation>
  );
}
